import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MessageSquare,
  CheckCircle,
  Video,
  Building,
  Briefcase,
  Handshake,
  Users,
  Newspaper,
  HelpCircle,
  Wrench,
  Globe,
  FileText,
  TrendingUp,
  MapPin,
} from 'lucide-react';
import CalendarPopup from '@/components/ui/CalendarPopup';
import {
  DiscoveryHero,
  QualificationProcess,
  CallAgenda,
  BookingSuccess,
  ContactInfo,
} from '@/components/discovery';
import { TrustFooter } from '@/components/common/TrustFooter';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface BookingForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  consultationType: 'general' | '';
  // Investment fields
  investmentBudget: string;
  investmentTimeline: string;
  // Live Opportunities fields
  opportunityInterest: string;
  deploymentTimeline: string;
  investmentCapacity: string;
  experienceLevel: string;
  opportunityQuestions: string;
  // General Inquiry fields
  inquiryType: string;
  // Contractor fields
  serviceCategory: string;
  servicesDescription: string;
  portfolioUrl: string;
  // Strategic Partner fields
  partnershipInterest: string;
  partnershipProposal: string;
  // Existing Partner fields
  partnerInquiryType: string;
  partnerReference: string;
  partnerDetails: string;
  // Media fields
  publication: string;
  mediaRequestType: string;
  mediaDeadline: string;
  mediaBrief: string;
  // Other fields
  otherSubject: string;
  otherMessage: string;
  preferredDate: string;
  preferredTime: string;
  meetingType: 'video' | 'phone' | '';
  message: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const consultationTypes = [
  {
    id: 'general',
    label: 'General Inquiry',
    desc: 'Questions about our services or approach',
    icon: Briefcase,
  },
];

const generalInquiryTypes = [
  { id: 'contractor', label: 'Contractor / Service Provider', desc: 'Offer your services to our projects', icon: Wrench },
  { id: 'partner', label: 'Strategic Partner', desc: 'Explore collaboration opportunities', icon: Handshake },
  { id: 'existing', label: 'Existing Partner Inquiry', desc: 'Questions about your current partnership', icon: Users },
  { id: 'media', label: 'Media / Press', desc: 'Press inquiries and media requests', icon: Newspaper },
  { id: 'other', label: 'Other Inquiry', desc: 'Something else entirely', icon: HelpCircle },
];

const meetingTypes = [
  { id: 'video', label: 'Google Meet', icon: Video },
  { id: 'phone', label: 'Phone Call', icon: Phone },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BookNowPage: React.FC = () => {
  const formRef = useRef<HTMLDivElement>(null);

  // Form State
  const [formData, setFormData] = useState<BookingForm>({
    name: '',
    email: '',
    phone: '',
    company: '',
    consultationType: '',
    // Investment fields
    investmentBudget: '',
    investmentTimeline: '',
    // Live Opportunities fields
    opportunityInterest: '',
    deploymentTimeline: '',
    investmentCapacity: '',
    experienceLevel: '',
    opportunityQuestions: '',
    // General Inquiry fields
    inquiryType: '',
    // Contractor fields
    serviceCategory: '',
    servicesDescription: '',
    portfolioUrl: '',
    // Strategic Partner fields
    partnershipInterest: '',
    partnershipProposal: '',
    // Existing Partner fields
    partnerInquiryType: '',
    partnerReference: '',
    partnerDetails: '',
    // Media fields
    publication: '',
    mediaRequestType: '',
    mediaDeadline: '',
    mediaBrief: '',
    // Other fields
    otherSubject: '',
    otherMessage: '',
    preferredDate: '',
    preferredTime: '',
    meetingType: '',
    message: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [wizardSubmitted, setWizardSubmitted] = useState<{
    eventId?: string;
    htmlLink?: string;
    date?: string;
    time?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);

  // Read URL query parameters for preselection
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const consultationParam = searchParams.get('consultation');
    const opportunityParam = searchParams.get('opportunity');

    if (consultationParam) {
      setFormData((prev) => ({
        ...prev,
        consultationType: consultationParam as 'general',
      }));

      // Scroll to form after a short delay to allow state to update
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }

  }, [searchParams]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) errors.name = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.email = 'Please enter a valid email address';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.consultationType)
      errors.consultationType = 'Please select a consultation type';

    // Validate based on consultation type
    if (formData.consultationType === 'general') {
      if (!formData.inquiryType)
        errors.inquiryType = 'Please select your inquiry type';

      // Validate based on inquiry type
      if (formData.inquiryType === 'contractor') {
        if (!formData.serviceCategory)
          errors.serviceCategory = 'Please select your service category';
        if (!formData.servicesDescription.trim())
          errors.servicesDescription = 'Please describe your services';
      } else if (formData.inquiryType === 'partner') {
        if (!formData.partnershipInterest)
          errors.partnershipInterest = 'Please select your partnership interest';
        if (!formData.partnershipProposal.trim())
          errors.partnershipProposal = 'Please describe your partnership proposal';
      } else if (formData.inquiryType === 'existing') {
        if (!formData.partnerInquiryType)
          errors.partnerInquiryType = 'Please select your inquiry type';
      } else if (formData.inquiryType === 'media') {
        if (!formData.publication.trim())
          errors.publication = 'Please enter your publication/outlet';
        if (!formData.mediaRequestType)
          errors.mediaRequestType = 'Please select your request type';
      } else if (formData.inquiryType === 'other') {
        if (!formData.otherSubject.trim())
          errors.otherSubject = 'Please enter a subject';
        if (!formData.otherMessage.trim())
          errors.otherMessage = 'Please enter your message';
      }
    }

    if (!formData.preferredDate)
      errors.preferredDate = 'Please select a preferred date';
    if (!formData.preferredTime)
      errors.preferredTime = 'Please select a preferred time';
    if (!formData.meetingType)
      errors.meetingType = 'Please select a meeting type';

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    let eventId: string | undefined;
    let htmlLink: string | undefined;

    // Book calendar event via API
    try {
      const response = await fetch('/api/calendar/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          date: formData.preferredDate,
          time: formData.preferredTime,
          duration: 30,
          meetingType:
            formData.meetingType === 'video' ? 'google-meet' : formData.meetingType,
          notes: [
            `Company: ${formData.company}`,
            `Consultation Type: ${formData.consultationType}`,
            formData.message ? `Message: ${formData.message}` : '',
            ...(formData.consultationType === 'general'
              ? [
                  `Inquiry Type: ${formData.inquiryType}`,
                  ...(formData.inquiryType === 'contractor'
                    ? [
                        `Service Category: ${formData.serviceCategory}`,
                        `Services Description: ${formData.servicesDescription}`,
                        formData.portfolioUrl ? `Portfolio URL: ${formData.portfolioUrl}` : '',
                      ]
                    : []),
                  ...(formData.inquiryType === 'partner'
                    ? [
                        `Partnership Interest: ${formData.partnershipInterest}`,
                        `Partnership Proposal: ${formData.partnershipProposal}`,
                      ]
                    : []),
                  ...(formData.inquiryType === 'existing'
                    ? [
                        `Partner Inquiry Type: ${formData.partnerInquiryType}`,
                        formData.partnerReference ? `Partner Reference: ${formData.partnerReference}` : '',
                        formData.partnerDetails ? `Details: ${formData.partnerDetails}` : '',
                      ]
                    : []),
                  ...(formData.inquiryType === 'media'
                    ? [
                        `Publication: ${formData.publication}`,
                        `Media Request Type: ${formData.mediaRequestType}`,
                        formData.mediaDeadline ? `Deadline: ${formData.mediaDeadline}` : '',
                        formData.mediaBrief ? `Brief: ${formData.mediaBrief}` : '',
                      ]
                    : []),
                  ...(formData.inquiryType === 'other'
                    ? [
                        `Subject: ${formData.otherSubject}`,
                        `Message: ${formData.otherMessage}`,
                      ]
                    : []),
                ]
              : []),
          ]
            .filter(Boolean)
            .join('\n'),
        }),
      });

      if (response.ok) {
        const result = await response.json();
        eventId = result.eventId;
        htmlLink = result.htmlLink;
      }
    } catch (error) {
      console.log('Calendar booking failed, proceeding with form submission:', error);
    }

    // Submit to consultation service (Supabase)
    try {
      const { createConsultation } = await import('@/services/consultationsService');
      await createConsultation({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        consultation_type: formData.consultationType || undefined,
        preferred_date: formData.preferredDate || null,
        preferred_time: formData.preferredTime || null,
        meeting_type: formData.meetingType || undefined,
        message: formData.message,
        event_id: eventId,
        event_html_link: htmlLink,
      });
    } catch (error) {
      console.error('Failed to save consultation:', error);
    }

    setIsSubmitting(false);
    setWizardSubmitted({
      eventId,
      htmlLink,
      date: formData.preferredDate,
      time: formData.preferredTime,
    });
    setSubmitted(true);
  };

  const clearError = (field: string) => {
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDateTimeSelect = (date: string, time: string) => {
    setFormData((prev) => ({
      ...prev,
      preferredDate: date,
      preferredTime: time,
    }));
    clearError('preferredDate');
    clearError('preferredTime');
  };

  const handleBookAnother = () => {
    setSubmitted(false);
    setWizardSubmitted(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      consultationType: '',
      investmentBudget: '',
      investmentTimeline: '',
      opportunityInterest: '',
      deploymentTimeline: '',
      investmentCapacity: '',
      experienceLevel: '',
      opportunityQuestions: '',
      inquiryType: '',
      serviceCategory: '',
      servicesDescription: '',
      portfolioUrl: '',
      partnershipInterest: '',
      partnershipProposal: '',
      partnerInquiryType: '',
      partnerReference: '',
      partnerDetails: '',
      publication: '',
      mediaRequestType: '',
      mediaDeadline: '',
      mediaBrief: '',
      otherSubject: '',
      otherMessage: '',
      preferredDate: '',
      preferredTime: '',
      meetingType: '',
      message: '',
    });
  };

  // ============================================================================
  // RENDER: SUCCESS STATE
  // ============================================================================

  if (submitted || wizardSubmitted) {
    return (
      <div className="relative bg-luxury-bg dark:bg-gradient-to-br dark:from-[#1a2744] dark:via-[#152238] dark:to-[#1F293D] text-luxury-text dark:text-luxury-text-dark">
        <div className="relative z-10">
          <BookingSuccess
            bookingData={wizardSubmitted || undefined}
            onBookAnother={handleBookAnother}
          />
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: MAIN PAGE
  // ============================================================================

  return (
    <div className="relative bg-luxury-bg dark:bg-gradient-to-br dark:from-[#1a2744] dark:via-[#152238] dark:to-[#1F293D] text-luxury-text dark:text-luxury-text-dark">
      <div className="relative z-10">
        {/* HERO SECTION */}
        <DiscoveryHero onScrollToForm={scrollToForm} />

        {/* QUALIFICATION PROCESS */}
        <QualificationProcess />

        {/* BOOKING FORM SECTION */}
        <section ref={formRef} className="py-12 sm:py-16 lg:py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
            {/* Form Header */}
            <div className="text-center mb-10 sm:mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center px-4 py-2 bg-luxury-gold/10 border border-luxury-gold/20 rounded-full mb-4"
              >
                <Calendar className="w-4 h-4 text-luxury-gold mr-2" />
                <span className="text-sm font-medium text-luxury-gold">
                  Schedule Your Call
                </span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-2xl sm:text-3xl md:text-4xl font-bold text-luxury-text dark:text-luxury-text-dark mb-4"
              >
                Book Your{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold to-luxury-gold-light">
                  Discovery Call
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-luxury-text-muted dark:text-luxury-text-muted-dark max-w-xl mx-auto"
              >
                Select a time that works for you. We'll send confirmation with your
                meeting link.
              </motion.p>
            </div>

            {/* Main Form Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-luxury-gold/20 p-4 sm:p-6 shadow-xl relative overflow-hidden"
            >
              {/* Decorative corner accents */}
              <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-luxury-gold/5 to-transparent rounded-bl-full" />
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-luxury-gold/5 to-transparent rounded-tr-full" />
              {/* Wizard Progress Steps */}
              <div className="mb-3 sm:mb-4">
                <div className="grid grid-cols-4 gap-2 sm:gap-3">
                  {[
                    { icon: User, label: 'Personal', sublabel: 'Information' },
                    { icon: MessageSquare, label: 'Consultation', sublabel: 'Type' },
                    { icon: Video, label: 'Meeting', sublabel: 'Details' },
                    { icon: Calendar, label: 'Schedule', sublabel: 'Call' },
                  ].map((step, idx) => {
                    const Icon = step.icon;
                    return (
                      <div key={idx} className="flex flex-col items-center text-center">
                        <div className="relative mb-1">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-luxury-gold/5 border border-luxury-gold/20 flex items-center justify-center">
                            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-luxury-gold" />
                          </div>
                          <div className="absolute -top-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-luxury-gold flex items-center justify-center text-white text-[9px] sm:text-[10px] font-semibold shadow-sm">
                            {idx + 1}
                          </div>
                        </div>
                        <span className="text-[9px] sm:text-[10px] font-semibold text-luxury-text dark:text-luxury-text-dark leading-tight">
                          {step.label}
                        </span>
                        <span className="text-[8px] sm:text-[9px] text-luxury-text-muted dark:text-luxury-text-muted-dark leading-tight">
                          {step.sublabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {/* STEP 1: Personal Information */}
                <div className="p-3 sm:p-4 bg-white/60 dark:bg-white/5 rounded-2xl border border-luxury-gold/20 shadow-md relative overflow-hidden">
                  {/* Decorative line */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />

                  <div className="flex items-center mb-3">
                    <div className="w-7 h-7 rounded-lg bg-luxury-gold/10 border border-luxury-gold/30 flex items-center justify-center mr-2">
                      <User className="w-3.5 h-3.5 text-luxury-gold" />
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-white">
                      Personal{' '}
                      <span className="text-luxury-gold">Information</span>
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-luxury-text dark:text-white mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, name: e.target.value }));
                          clearError('name');
                        }}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm"
                        placeholder="Enter your full name"
                      />
                      {formErrors.name && (
                        <p className="text-red-500 text-[10px] sm:text-xs mt-1 font-semibold">{formErrors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-luxury-text dark:text-white mb-1.5">
                        Email Address *
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, email: e.target.value }));
                            clearError('email');
                          }}
                          className="w-full px-3 py-2 pl-9 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm"
                          placeholder="your.email@example.com"
                        />
                        <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-luxury-gold/70" />
                      </div>
                      {formErrors.email && (
                        <p className="text-red-500 text-[10px] sm:text-xs mt-1 font-semibold">{formErrors.email}</p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-luxury-text dark:text-white mb-1.5">
                        Phone Number *
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => {
                            setFormData((prev) => ({ ...prev, phone: e.target.value }));
                            clearError('phone');
                          }}
                          className="w-full px-3 py-2 pl-9 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm"
                          placeholder="+44 20 1234 5678"
                        />
                        <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-luxury-gold/70" />
                      </div>
                      {formErrors.phone && (
                        <p className="text-red-500 text-[10px] sm:text-xs mt-1 font-semibold">{formErrors.phone}</p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-luxury-text dark:text-white mb-1.5">
                        Company (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.company}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, company: e.target.value }))
                          }
                          className="w-full px-3 py-2 pl-9 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm"
                          placeholder="Your company name"
                        />
                        <Building className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-luxury-gold/60" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 2: Consultation Type */}
                <div className="p-3 sm:p-4 bg-white/60 dark:bg-white/5 rounded-2xl border border-luxury-gold/20 shadow-md relative overflow-hidden">
                  {/* Decorative line */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />

                  <div className="flex items-center mb-2.5">
                    <div className="w-7 h-7 rounded-lg bg-luxury-gold/10 border border-luxury-gold/30 flex items-center justify-center mr-2">
                      <MessageSquare className="w-3.5 h-3.5 text-luxury-gold" />
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-white">
                      Consultation <span className="text-luxury-gold">Type</span>
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {consultationTypes.map((type) => {
                      const IconComponent = type.icon;
                      const isSelected = formData.consultationType === type.id;
                      return (
                        <label
                          key={type.id}
                          className={`relative flex flex-col items-center p-2.5 rounded-lg cursor-pointer transition-all duration-300 ${
                            isSelected
                              ? 'bg-luxury-gold text-white shadow-md border-2 border-luxury-gold'
                              : 'bg-white dark:bg-white/5 border-2 border-luxury-gold/20 hover:border-luxury-gold/40 hover:bg-luxury-gold/5 dark:hover:bg-luxury-gold/10'
                          }`}
                        >
                          <input
                            type="radio"
                            name="consultationType"
                            value={type.id}
                            checked={isSelected}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                consultationType: e.target.value as any,
                              }));
                              clearError('consultationType');
                            }}
                            className="sr-only"
                          />
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${
                              isSelected
                                ? 'bg-white/20'
                                : 'bg-luxury-gold/10 border border-luxury-gold/20'
                            }`}
                          >
                            <IconComponent
                              className={`w-4 h-4 ${
                                isSelected ? 'text-white' : 'text-luxury-gold'
                              }`}
                            />
                          </div>
                          <div
                            className={`font-semibold text-[10px] sm:text-xs text-center leading-tight ${
                              isSelected
                                ? 'text-white'
                                : 'text-luxury-text dark:text-white'
                            }`}
                          >
                            {type.label}
                          </div>
                          <div
                            className={`text-[8px] sm:text-[9px] text-center mt-0.5 leading-tight ${
                              isSelected
                                ? 'text-white/90'
                                : 'text-luxury-text-muted dark:text-luxury-text-muted-dark'
                            }`}
                          >
                            {type.desc}
                          </div>
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {formErrors.consultationType && (
                    <p className="text-red-500 text-[10px] sm:text-xs mt-1.5 font-semibold">
                      {formErrors.consultationType}
                    </p>
                  )}
                </div>



                {/* General Inquiry Section */}
                {formData.consultationType === 'general' && (
                  <div className="p-3 sm:p-4 bg-white/60 dark:bg-white/5 rounded-2xl border border-luxury-gold/20 shadow-md relative overflow-hidden">
                    {/* Decorative line */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />

                    <div className="flex items-center mb-2">
                      <div className="w-7 h-7 rounded-lg bg-luxury-gold/10 border border-luxury-gold/30 flex items-center justify-center mr-2">
                        <Briefcase className="w-3.5 h-3.5 text-luxury-gold" />
                      </div>
                      <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-white">
                        How Can We <span className="text-luxury-gold">Help?</span>
                      </h3>
                    </div>

                    {/* Inquiry Type Selection */}
                    <div className="space-y-2 mb-3">
                      {generalInquiryTypes.map((type) => {
                        const IconComponent = type.icon;
                        const isSelected = formData.inquiryType === type.id;
                        return (
                          <label
                            key={type.id}
                            className={`relative flex items-center p-2.5 rounded-xl cursor-pointer transition-all duration-300 ${
                              isSelected
                                ? 'bg-luxury-gold text-white shadow-md border-2 border-luxury-gold'
                                : 'bg-white dark:bg-white/5 border border-luxury-gold/20 hover:border-luxury-gold/40 hover:bg-luxury-gold/5 dark:hover:bg-luxury-gold/10'
                            }`}
                          >
                            <input
                              type="radio"
                              name="inquiryType"
                              value={type.id}
                              checked={isSelected}
                              onChange={(e) => {
                                setFormData((prev) => ({ ...prev, inquiryType: e.target.value }));
                                clearError('inquiryType');
                              }}
                              className="sr-only"
                            />
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${isSelected ? 'bg-white/20' : 'bg-luxury-gold/10 border border-luxury-gold/20'}`}>
                              <IconComponent className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-luxury-gold'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-[10px] sm:text-xs font-semibold ${isSelected ? 'text-white' : 'text-luxury-text dark:text-white'}`}>{type.label}</div>
                              <div className={`text-[8px] sm:text-[9px] ${isSelected ? 'text-white/90' : 'text-luxury-text-muted dark:text-luxury-text-muted-dark'}`}>{type.desc}</div>
                            </div>
                            {isSelected && <CheckCircle className="w-4 h-4 text-white ml-2" />}
                          </label>
                        );
                      })}
                    </div>
                    {formErrors.inquiryType && <p className="text-red-500 text-xs mb-4 font-semibold">{formErrors.inquiryType}</p>}

                    {/* Contractor Fields */}
                    {formData.inquiryType === 'contractor' && (
                      <div className="space-y-3 pt-3 border-t border-luxury-gold/20">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Service Category *</label>
                            <select
                              value={formData.serviceCategory}
                              onChange={(e) => { setFormData((prev) => ({ ...prev, serviceCategory: e.target.value })); clearError('serviceCategory'); }}
                              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-gray-100 transition-all appearance-none cursor-pointer text-sm"
                              style={{ colorScheme: 'light' }}
                            >
                              <option value="">Select category</option>
                              <option value="construction">Construction</option>
                              <option value="architecture">Architecture & Design</option>
                              <option value="legal">Legal</option>
                              <option value="finance">Finance & Accounting</option>
                              <option value="project-management">Project Management</option>
                              <option value="surveying">Surveying</option>
                              <option value="other">Other</option>
                            </select>
                            {formErrors.serviceCategory && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.serviceCategory}</p>}
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Portfolio/Website</label>
                            <input type="url" value={formData.portfolioUrl} onChange={(e) => setFormData((prev) => ({ ...prev, portfolioUrl: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm" placeholder="https://..." />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Brief Description of Services *</label>
                          <textarea value={formData.servicesDescription} onChange={(e) => { setFormData((prev) => ({ ...prev, servicesDescription: e.target.value })); clearError('servicesDescription'); }} rows={3} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all resize-none text-sm" placeholder="Describe your services..." />
                          {formErrors.servicesDescription && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.servicesDescription}</p>}
                        </div>
                      </div>
                    )}

                    {/* Strategic Partner Fields */}
                    {formData.inquiryType === 'partner' && (
                      <div className="space-y-3 pt-3 border-t border-luxury-gold/20">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Partnership Interest *</label>
                          <select
                            value={formData.partnershipInterest}
                            onChange={(e) => { setFormData((prev) => ({ ...prev, partnershipInterest: e.target.value })); clearError('partnershipInterest'); }}
                            className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-gray-100 transition-all appearance-none cursor-pointer text-sm"
                            style={{ colorScheme: 'light' }}
                          >
                            <option value="">Select interest</option>
                            <option value="jv">Joint Venture Opportunities</option>
                            <option value="sourcing">Deal Sourcing Collaboration</option>
                            <option value="capital">Capital Partnership</option>
                            <option value="technology">Technology / Platform Integration</option>
                            <option value="other">Other</option>
                          </select>
                          {formErrors.partnershipInterest && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.partnershipInterest}</p>}
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Partnership Proposal *</label>
                          <textarea value={formData.partnershipProposal} onChange={(e) => { setFormData((prev) => ({ ...prev, partnershipProposal: e.target.value })); clearError('partnershipProposal'); }} rows={3} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all resize-none text-sm" placeholder="Briefly describe your collaboration idea..." />
                          {formErrors.partnershipProposal && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.partnershipProposal}</p>}
                        </div>
                      </div>
                    )}

                    {/* Existing Partner Fields */}
                    {formData.inquiryType === 'existing' && (
                      <div className="space-y-3 pt-3 border-t border-luxury-gold/20">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Inquiry Type *</label>
                            <select
                              value={formData.partnerInquiryType}
                              onChange={(e) => { setFormData((prev) => ({ ...prev, partnerInquiryType: e.target.value })); clearError('partnerInquiryType'); }}
                              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-gray-100 transition-all appearance-none cursor-pointer text-sm"
                              style={{ colorScheme: 'light' }}
                            >
                              <option value="">Select type</option>
                              <option value="portfolio">Portfolio Update Request</option>
                              <option value="opportunity">New Opportunity Discussion</option>
                              <option value="documentation">Documentation Request</option>
                              <option value="general">General Question</option>
                            </select>
                            {formErrors.partnerInquiryType && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.partnerInquiryType}</p>}
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Partner Reference</label>
                            <input type="text" value={formData.partnerReference} onChange={(e) => setFormData((prev) => ({ ...prev, partnerReference: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm" placeholder="Your partner ID or name" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Details</label>
                          <textarea value={formData.partnerDetails} onChange={(e) => setFormData((prev) => ({ ...prev, partnerDetails: e.target.value }))} rows={2} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all resize-none text-sm" placeholder="Additional details..." />
                        </div>
                      </div>
                    )}

                    {/* Media Fields */}
                    {formData.inquiryType === 'media' && (
                      <div className="space-y-3 pt-3 border-t border-luxury-gold/20">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Publication/Outlet *</label>
                            <input type="text" value={formData.publication} onChange={(e) => { setFormData((prev) => ({ ...prev, publication: e.target.value })); clearError('publication'); }} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm" placeholder="Publication name" />
                            {formErrors.publication && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.publication}</p>}
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Request Type *</label>
                            <select
                              value={formData.mediaRequestType}
                              onChange={(e) => { setFormData((prev) => ({ ...prev, mediaRequestType: e.target.value })); clearError('mediaRequestType'); }}
                              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-gray-100 transition-all appearance-none cursor-pointer text-sm"
                              style={{ colorScheme: 'light' }}
                            >
                              <option value="">Select type</option>
                              <option value="interview">Interview Request</option>
                              <option value="comment">Expert Comment</option>
                              <option value="feature">Feature / Profile</option>
                              <option value="event">Event Coverage</option>
                              <option value="other">Other</option>
                            </select>
                            {formErrors.mediaRequestType && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.mediaRequestType}</p>}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Deadline</label>
                            <input type="date" value={formData.mediaDeadline} onChange={(e) => setFormData((prev) => ({ ...prev, mediaDeadline: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-white transition-all text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Brief</label>
                            <input type="text" value={formData.mediaBrief} onChange={(e) => setFormData((prev) => ({ ...prev, mediaBrief: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm" placeholder="Brief summary..." />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Other Inquiry Fields */}
                    {formData.inquiryType === 'other' && (
                      <div className="space-y-3 pt-3 border-t border-luxury-gold/20">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Subject *</label>
                          <input type="text" value={formData.otherSubject} onChange={(e) => { setFormData((prev) => ({ ...prev, otherSubject: e.target.value })); clearError('otherSubject'); }} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all text-sm" placeholder="Subject of your inquiry" />
                          {formErrors.otherSubject && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.otherSubject}</p>}
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1.5">Message *</label>
                          <textarea value={formData.otherMessage} onChange={(e) => { setFormData((prev) => ({ ...prev, otherMessage: e.target.value })); clearError('otherMessage'); }} rows={3} className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-gray-900 dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all resize-none text-sm" placeholder="Your message..." />
                          {formErrors.otherMessage && <p className="text-red-500 text-[10px] sm:text-xs mt-1">{formErrors.otherMessage}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 3: Meeting Details */}
                <div className="p-2.5 sm:p-3.5 bg-white/60 dark:bg-white/5 rounded-2xl border border-luxury-gold/20 shadow-md relative overflow-hidden">
                  {/* Decorative line */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />

                  <div className="flex items-center mb-2">
                    <div className="w-7 h-7 rounded-lg bg-luxury-gold/10 border border-luxury-gold/30 flex items-center justify-center mr-2">
                      <Calendar className="w-3.5 h-3.5 text-luxury-gold" />
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-white">
                      Meeting <span className="text-luxury-gold">Details</span>
                    </h3>
                  </div>

                  {/* Calendar Selection Button */}
                  <button
                    type="button"
                    onClick={() => setShowCalendarPopup(true)}
                    className="w-full p-2 sm:p-2.5 rounded-lg bg-white dark:bg-white/5 border-2 border-luxury-gold/20 hover:border-luxury-gold/40 hover:bg-luxury-gold/5 transition-all duration-300"
                  >
                    <div className="flex items-center justify-center space-x-2.5">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-luxury-gold/10 border border-luxury-gold/20 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-luxury-gold" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        {formData.preferredDate && formData.preferredTime ? (
                          <div>
                            <div className="font-semibold text-xs sm:text-sm text-luxury-text dark:text-white">
                              {new Date(
                                formData.preferredDate + 'T12:00:00'
                              ).toLocaleDateString('en-GB', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </div>
                            <div className="text-luxury-gold font-semibold flex items-center mt-0.5">
                              <Clock className="w-3 h-3 mr-1" />
                              <span className="text-xs">{formData.preferredTime}</span>
                            </div>
                            <div className="text-[9px] sm:text-[10px] text-luxury-text-muted dark:text-luxury-text-muted-dark mt-0.5">
                              Click to change
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-semibold text-xs sm:text-sm text-luxury-text dark:text-white mb-0.5">
                              Choose Your Preferred Date & Time
                            </div>
                            <div className="text-[10px] sm:text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                              Click to open calendar
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                  {(formErrors.preferredDate || formErrors.preferredTime) && (
                    <p className="text-red-500 text-[10px] sm:text-xs mt-1.5 font-semibold">
                      {formErrors.preferredDate || formErrors.preferredTime}
                    </p>
                  )}
                </div>

                {/* STEP 4: Meeting Type */}
                <div className="p-2.5 sm:p-3.5 bg-white/60 dark:bg-white/5 rounded-2xl border border-luxury-gold/20 shadow-md relative overflow-hidden">
                  {/* Decorative line */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />

                  <div className="flex items-center mb-2">
                    <div className="w-7 h-7 rounded-lg bg-luxury-gold/10 border border-luxury-gold/30 flex items-center justify-center mr-2">
                      <Video className="w-3.5 h-3.5 text-luxury-gold" />
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-white">
                      Meeting <span className="text-luxury-gold">Type</span>
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {meetingTypes.map((type) => {
                      const IconComponent = type.icon;
                      const isSelected = formData.meetingType === type.id;
                      return (
                        <label
                          key={type.id}
                          className={`relative flex flex-col items-center p-2 rounded-lg cursor-pointer transition-all duration-300 ${
                            isSelected
                              ? 'bg-luxury-gold text-white shadow-md border-2 border-luxury-gold'
                              : 'bg-white dark:bg-white/5 border-2 border-luxury-gold/20 hover:border-luxury-gold/40 hover:shadow-md hover:bg-luxury-gold/5 dark:hover:bg-luxury-gold/10'
                          }`}
                        >
                          <input
                            type="radio"
                            name="meetingType"
                            value={type.id}
                            checked={isSelected}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                meetingType: e.target.value as any,
                              }));
                              clearError('meetingType');
                            }}
                            className="sr-only"
                          />
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center mb-1.5 ${
                              isSelected
                                ? 'bg-white/20'
                                : 'bg-luxury-gold/10 border border-luxury-gold/20'
                            }`}
                          >
                            <IconComponent
                              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                                isSelected ? 'text-white' : 'text-luxury-gold'
                              }`}
                            />
                          </div>
                          <div
                            className={`font-semibold text-[10px] sm:text-xs text-center leading-tight ${
                              isSelected
                                ? 'text-white'
                                : 'text-luxury-text dark:text-white'
                            }`}
                          >
                            {type.label}
                          </div>
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5">
                              <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                            </div>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {formErrors.meetingType && (
                    <p className="text-red-500 text-[10px] sm:text-xs mt-1.5 font-semibold">{formErrors.meetingType}</p>
                  )}
                </div>

                {/* Additional Message */}
                <div className="p-2.5 sm:p-3.5 bg-white/60 dark:bg-white/5 rounded-2xl border border-luxury-gold/20 shadow-md relative overflow-hidden">
                  {/* Decorative line */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/30 to-transparent" />

                  <div className="flex items-center mb-2">
                    <div className="w-7 h-7 rounded-lg bg-luxury-gold/10 border border-luxury-gold/30 flex items-center justify-center mr-2">
                      <MessageSquare className="w-3.5 h-3.5 text-luxury-gold" />
                    </div>
                    <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-white">
                      Additional{' '}
                      <span className="text-luxury-gold">Information</span>{' '}
                      <span className="text-[10px] sm:text-xs font-normal text-luxury-text-muted dark:text-luxury-text-muted-dark">
                        (Optional)
                      </span>
                    </h3>
                  </div>
                  <textarea
                    value={formData.message}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, message: e.target.value }))
                    }
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-white/5 border border-luxury-gold/20 focus:border-luxury-gold/40 focus:ring-2 focus:ring-luxury-gold/20 text-luxury-text dark:text-white placeholder-luxury-text-muted/50 dark:placeholder-luxury-text-muted-dark/50 transition-all resize-none text-sm"
                    placeholder="Any specific topics you'd like to discuss or questions you have..."
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full inline-flex items-center justify-center px-8 py-4 text-base font-bold text-luxury-text tracking-wide transition-all duration-300 bg-gradient-to-r from-luxury-gold via-luxury-gold-light to-luxury-gold rounded-xl hover:shadow-gold-intense hover:-translate-y-1 shadow-gold-lg overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 border-2 border-luxury-gold"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                    <span className="relative flex items-center">
                      {isSubmitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Confirming Your Call...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4 mr-2" />
                          <span className="text-sm sm:text-base">Confirm Your Discovery Call</span>
                        </>
                      )}
                    </span>
                  </button>

                  {/* Trust Indicators */}
                  <div className="flex flex-wrap justify-center items-center gap-4 mt-6 text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Secure & Confidential
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-luxury-gold" />
                      30 minute call
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-luxury-gold" />
                      Instant confirmation
                    </div>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        </section>

        {/* CALL AGENDA */}
        <CallAgenda />

        {/* CONTACT INFORMATION */}
        <ContactInfo />

        {/* Trust Footer */}
        <TrustFooter />
      </div>

      {/* Calendar Popup */}
      <CalendarPopup
        isOpen={showCalendarPopup}
        onClose={() => setShowCalendarPopup(false)}
        onSelect={handleDateTimeSelect}
        selectedDate={formData.preferredDate}
        selectedTime={formData.preferredTime}
      />
    </div>
  );
};

export default BookNowPage;
